import { HStack } from '@chakra-ui/react';

import { ClientType } from '__generated__/GQL';
import { FCWithChildren } from 'common/types';
import Button from 'components/_core/Button';
import Chip from 'components/_core/Chip';
import Heading from 'components/_core/Heading';
import SunshineModal from 'components/_core/SunshineModal';
import Form from 'components/Form';
import { useModal } from 'components/ModalProvider/hooks';
import useToast from 'components/ToastProvider/useToast';

import {
  CLIENT_SAVE_CHANGES_ALERT_KEY,
  ClientForm,
  useClientFormModalContext,
} from '../../hooks/useClientFormModalContext';
import useCreateClient from './hooks/useCreateClient';
import useUpdateClient from './hooks/useUpdateClient';
import locales from './locales';
import * as S from './styles';

const FORM_ID = 'client-form';

interface ClientFormWrapperProps {
  canSubmit: boolean;
  clientInput: ClientForm;
  showAlert: () => void;
}

/**
 * This form wrapper handles the form submission and
 * contains the toggle to switch between the organization and individual form
 */
const ClientFormWrapper: FCWithChildren<ClientFormWrapperProps> = ({
  canSubmit,
  children,
  clientInput,
  showAlert,
}) => {
  const { client, isClientOrganization, setClient, setIsClientOrganization } =
    useClientFormModalContext();

  const { showToast } = useToast();
  const { hideModal } = useModal();

  const { clientId } = client;
  const { createClient, loading: isCreatingClient } = useCreateClient();

  // Can only update a client if there's a clientId
  const { loading: isUpdatingClient, updateClient } = useUpdateClient(
    clientId as string,
  );

  const loading = isCreatingClient || isUpdatingClient;
  const isNewClient = !clientId;

  const onSubmit = async () => {
    // Prevent submission if the form is invalid, this can happen when a user
    // press the "return" key on the keyboard.
    if (!canSubmit) {
      return;
    }

    let clientName: string;

    if (isNewClient) {
      // Create new client
      const { data } = await createClient(clientInput);
      clientName = data?.createClient?.client?.name || '';
      hideModal();
    } else {
      if (!clientId) {
        throw new Error('clientId is undefined in ClientFormModal');
      }

      // Update existing client
      const { data } = await updateClient(clientInput);
      clientName = data?.updateClient?.client?.name || '';

      if (localStorage.getItem(CLIENT_SAVE_CHANGES_ALERT_KEY)) {
        hideModal();
      } else {
        showAlert();
      }
    }

    showToast({
      message: locales.formatString(
        isNewClient
          ? locales.createClientSuccessMessage
          : locales.editClientSuccessMessage,
        clientName,
      ) as string,
      title: isNewClient
        ? locales.createClientSuccessTitle
        : locales.editClientSuccessTitle,
      type: 'success',
    });
  };

  return (
    <>
      <SunshineModal.Body>
        <Form id={FORM_ID} onValidSubmit={onSubmit}>
          <S.ClientLoopImage />

          <Heading>
            {isNewClient ? locales.createClientTitle : locales.editClientTitle}
          </Heading>

          <S.SecondaryText>{locales.clientIs}</S.SecondaryText>

          <S.FiltersContainer>
            <HStack spacing="space-8">
              <Chip
                icon="company"
                isActive={isClientOrganization}
                onClick={() => {
                  setIsClientOrganization(true);
                  setClient({ ...client, type: ClientType.Organization });
                }}
              >
                {locales.company}
              </Chip>

              <Chip
                icon="profile"
                isActive={!isClientOrganization}
                onClick={() => {
                  setIsClientOrganization(false);
                  setClient({ ...client, type: ClientType.PrivateIndividual });
                }}
              >
                {locales.individual}
              </Chip>
            </HStack>
          </S.FiltersContainer>

          {children}
        </Form>
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button
          form={FORM_ID}
          icon="arrow-right"
          isDisabled={!canSubmit}
          isLoading={loading}
          type="submit"
        >
          {isNewClient ? locales.createClientCta : locales.editClientCta}
        </Button>
      </SunshineModal.Footer>
    </>
  );
};

export default ClientFormWrapper;
